// Global Instructions Rule Applied!

import { FieldTypeEnum, SelectTypeEnum, SelectFieldConfig } from '../../../core/components/InputManager/types.ts'
import type { CountryChangePatch } from './types.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import { RoundEntryFields } from '../../../state_manager/data_models/round_entry.ts'
import { AccountRoleEntryFields } from '../../../state_manager/data_models/account_role_entry.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'

/**
 * Maps CompaniesTable column definitions to InputManager field configurations
 * Handles dynamic field type detection and option mapping
 */

/**
 * Maps a single column to InputManager field configuration
 */
export function mapColumnToInputManagerField(column: any, record: any, helpers: any, fieldType: FieldTypeEnum) {
  const { getCurrencyById, getLookupOptions, getRoleOptions, getFundOptions, getShareRegisterOptions } = helpers

  const columnKey = column.dataIndex || column.key
  const columnTitle = column.title
  const isRequired = column.required || false
  const isEditable = column.editable !== false

  // Get current value from record, handling nested structures
  const getCurrentValue = () => {
    if (
      columnKey === AccountRecordFields.accounts__business_city_label ||
      columnKey === AccountRecordFields.accounts__business_city_code ||
      columnKey === 'accounts__business_city_code__label' ||
      columnKey === 'business_city'
    ) {
      const cityCode = record[AccountRecordFields.accounts__business_city_code]
      const cityLabel =
        record[AccountRecordFields.accounts__business_city_label] || record['accounts__business_city_code__label']
      const countryCode = record[AccountRecordFields.accounts__business_country_code]
      const state = record[AccountRecordFields.accounts__business_stateprovince]

      if (cityCode && cityLabel) {
        return {
          value: cityCode,
          name: cityLabel,
          label: cityLabel,
          data: { countryCode: countryCode || null, state: state || null }
        }
      }
      return cityLabel || cityCode || ''
    }
    if (
      columnKey === AccountRecordFields.accounts__business_country_label ||
      columnKey === AccountRecordFields.accounts__business_country_code ||
      columnKey === 'accounts__business_country_code__label' ||
      columnKey === 'business_country'
    ) {
      const countryCode = record[AccountRecordFields.accounts__business_country_code]
      const countryLabel =
        record[AccountRecordFields.accounts__business_country_label] ||
        record[ProspectRecordFields.accounts__business_country_code__label]

      if (countryCode && countryLabel) {
        return { value: countryCode, label: countryLabel }
      }
      return countryLabel || countryCode || ''
    }

    // Handle nested fields from prog__recent_round (for valuation) and prog__latest_round (for general round info)
    if (columnKey === 'valuation') {
      // Special handling for valuation - return object with pre/post values
      const recentRound = record[AccountRecordFields.prog__recent_round]
      return {
        preMoneyValue: recentRound?.[RoundEntryFields.rounds__pre_money_valuation] || null,
        postMoneyValue: recentRound?.[RoundEntryFields.rounds__post_money_valuation] || null,
        currencySymbol:
          getCurrencyById(recentRound?.[RoundEntryFields.rounds__currency] || record.currency)?.symbol || '$'
      }
    }
    if (columnKey === 'valuation_round') {
      // Valuation Round name comes from prog__recent_round (the round with valuation)
      const recentRound = record[AccountRecordFields.prog__recent_round]
      return recentRound?.[RoundEntryFields.rounds__name] || ''
    }
    if (columnKey === 'latest_round') {
      // Latest round name comes from most recent round with date (prog__latest_round)
      const latestRound = record[AccountRecordFields.prog__latest_round]
      return latestRound?.[RoundEntryFields.rounds__name] || ''
    }
    if (columnKey === 'funding_stage') {
      // Funding stage comes from most recent round with date (prog__latest_round)
      const latestRound = record[AccountRecordFields.prog__latest_round]
      return latestRound?.[RoundEntryFields.rounds__funding_stage] || ''
    }
    if (columnKey === RoundEntryFields.rounds__raise_amount || columnKey === 'rounds__raise_amount') {
      const latestRound = record[AccountRecordFields.prog__latest_round]
      return latestRound?.[RoundEntryFields.rounds__raise_amount] ?? ''
    }
    if (columnKey === RoundEntryFields.rounds__security_type || columnKey === 'rounds__security_type') {
      const latestRound = record[AccountRecordFields.prog__latest_round]
      return (
        latestRound?.['rounds__security_type__label'] ?? latestRound?.[RoundEntryFields.rounds__security_type] ?? ''
      )
    }
    if (columnKey === 'prog__investment_stats') {
      const stats = record[AccountRecordFields.prog__investment_stats]
      if (!stats || typeof stats !== 'object') return []
      const ids = (stats as Record<string, any>).funds__id
      if (Array.isArray(ids)) {
        return ids.filter((id: any): id is string => typeof id === 'string' && id.trim() !== '')
      }
      if (typeof ids === 'string' && ids.trim() !== '') {
        return [ids]
      }
      return []
    }
    if (columnKey === 'role') {
      // Extract account_role_choice_id from prog__account_roles array
      const roles = record[AccountRecordFields.prog__account_roles] || []
      if (Array.isArray(roles) && roles.length > 0) {
        // Return array of account_role_choice_id values
        return roles
          .map((role: any) => {
            if (typeof role === 'string') return role
            if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
              return role.account_roles__account_role_choice_id
            }
            return null
          })
          .filter((id: any): id is string => id !== null)
      }
      return []
    }
    return record[columnKey] || ''
  }

  const selectConfig = (): Pick<SelectFieldConfig, 'selectType' | 'options' | 'lookupCategory'> => {
    switch (columnKey) {
      case AccountRecordFields.accounts__industry:
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_INDUSTRY
        }
      case AccountRecordFields.accounts__share_register_id:
        return {
          selectType: undefined,
          options: getShareRegisterOptions ? getShareRegisterOptions() : []
        }
      case 'role':
        return {
          selectType: undefined,
          options: getRoleOptions ? getRoleOptions() : []
        }
      case 'prog__investment_stats':
        return {
          selectType: undefined,
          options: getFundOptions ? getFundOptions() : []
        }
      case 'funding_stage':
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE
        }
      case RoundEntryFields.rounds__security_type:
      case 'rounds__security_type':
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE
        }
      default:
        return {
          selectType: undefined,
          options: []
        }
    }
  }

  // Get currency symbol for currency and valuation fields
  const getCurrencySymbol = () => {
    // For valuation, get currency from recent round (which has valuation)
    if (columnKey === 'valuation') {
      const recentRound = record[AccountRecordFields.prog__recent_round]
      const currencyId = recentRound?.[RoundEntryFields.rounds__currency] || record.currency
      return getCurrencyById(currencyId)?.symbol || '$'
    }
    if (columnKey === RoundEntryFields.rounds__raise_amount || columnKey === 'rounds__raise_amount') {
      const latestRound = record[AccountRecordFields.prog__latest_round]
      const currencyId = latestRound?.[RoundEntryFields.rounds__currency] || record.currency
      return getCurrencyById(currencyId)?.symbol || '$'
    }
    if (fieldType === FieldTypeEnum.CURRENCY) {
      return getCurrencyById(record.currency)?.symbol || '$'
    }
    return '$'
  }

  // Get country filter for city field
  const getCountryFilter = () => {
    if (
      columnKey === AccountRecordFields.accounts__business_city_label ||
      columnKey === AccountRecordFields.accounts__business_city_code ||
      columnKey === 'accounts__business_city_code__label' ||
      columnKey === 'business_city'
    ) {
      return record[AccountRecordFields.accounts__business_country_code] || null
    }
    return null
  }

  // Map column keys to proper field keys (path__fieldname format for API)
  const getFieldKey = () => {
    if (columnKey === 'role') {
      return 'accounts__roles'
    }
    if (
      columnKey === 'business_city' ||
      columnKey === AccountRecordFields.accounts__business_city_code ||
      columnKey === 'accounts__business_city_code__label'
    ) {
      return AccountRecordFields.accounts__business_city_label
    }
    if (
      columnKey === 'business_country' ||
      columnKey === AccountRecordFields.accounts__business_country_code ||
      columnKey === 'accounts__business_country_code__label'
    ) {
      return AccountRecordFields.accounts__business_country_label
    }
    if (columnKey === 'funding_stage') {
      return RoundEntryFields.rounds__funding_stage
    }
    if (columnKey === 'latest_round' || columnKey === 'valuation_round') {
      return RoundEntryFields.rounds__name
    }
    return columnKey
  }

  const fieldKey = getFieldKey()

  // Extract roundId for Round-related fields
  const isRoundField =
    columnKey === 'valuation' ||
    columnKey === 'valuation_round' ||
    columnKey === 'latest_round' ||
    columnKey === 'funding_stage' ||
    columnKey === RoundEntryFields.rounds__raise_amount ||
    columnKey === 'rounds__raise_amount' ||
    columnKey === RoundEntryFields.rounds__security_type ||
    columnKey === 'rounds__security_type'

  let roundId: string | null = null
  if (columnKey === 'valuation' || columnKey === 'valuation_round') {
    const recentRound = record[AccountRecordFields.prog__recent_round]
    roundId = recentRound?.[RoundEntryFields.rounds__id] || null
  } else if (
    columnKey === 'latest_round' ||
    columnKey === 'funding_stage' ||
    columnKey === RoundEntryFields.rounds__raise_amount ||
    columnKey === 'rounds__raise_amount' ||
    columnKey === RoundEntryFields.rounds__security_type ||
    columnKey === 'rounds__security_type'
  ) {
    const latestRound = record[AccountRecordFields.prog__latest_round]
    roundId = latestRound?.[RoundEntryFields.rounds__id] || null
  }

  const baseConfig = {
    key: fieldKey,
    label: columnTitle,
    value: getCurrentValue(),
    type: fieldType,
    required: isRequired,
    editable: isRoundField ? isEditable && !!roundId : isEditable
  }

  // Add type-specific configurations
  switch (fieldType) {
    case FieldTypeEnum.CURRENCY: {
      const currencyConfig: any = {
        ...baseConfig,
        currencySymbol: getCurrencySymbol(),
        min: 0,
        step: 0.01
      }
      const isRoundRaise = columnKey === RoundEntryFields.rounds__raise_amount || columnKey === 'rounds__raise_amount'
      if (isRoundRaise && roundId) {
        currencyConfig.patchEndpoint = 'rounds'
        currencyConfig.patchId = roundId
        currencyConfig.patchFields = [{ sourceKey: 'value', targetField: 'raise_amount' }]
        currencyConfig.valueTransformer = (value: any) => ({
          raise_amount: value,
          [RoundEntryFields.rounds__raise_amount]: value,
          id: roundId
        })
      }
      return currencyConfig
    }

    case FieldTypeEnum.NUMBER:
      return {
        ...baseConfig,
        min: 0,
        step: 1
      }

    case FieldTypeEnum.SELECT:
      const forceTagDisplay = columnKey === 'role' || columnKey === 'prog__investment_stats'
      const selectFieldConfig: SelectFieldConfig = {
        ...baseConfig,
        ...selectConfig(),
        type: FieldTypeEnum.SELECT,
        allowClear: true,
        tagDisplay: forceTagDisplay ? true : column.tagDisplay || false
      } as SelectFieldConfig

      if (columnKey === 'role' || columnKey === 'prog__investment_stats') {
        selectFieldConfig.mode = 'multiple'
      }
      if (columnKey === 'role') {
        selectFieldConfig.patchEndpoint = 'accounts'
        selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
          // Transform array of role IDs to objects with actions
          const newRoleIds = Array.isArray(value) ? value : []

          // Get current roles from record
          const currentRoles = currentRecord?.[AccountRecordFields.prog__account_roles] || []
          const currentRoleMap = new Map<string, any>()

          // Build map of current roles by account_role_choice_id
          currentRoles.forEach((role: any) => {
            if (typeof role === 'object' && role[AccountRoleEntryFields.account_roles__account_role_choice_id]) {
              const roleChoiceId = role[AccountRoleEntryFields.account_roles__account_role_choice_id]
              currentRoleMap.set(roleChoiceId, role)
            } else if (typeof role === 'string') {
              // Legacy format: just the ID
              currentRoleMap.set(role, { [AccountRoleEntryFields.account_roles__account_role_choice_id]: role })
            }
          })

          // Build map of new roles
          const newRoleMap = new Map<string, boolean>()
          newRoleIds.forEach((roleId: string) => {
            if (roleId) {
              newRoleMap.set(roleId, true)
            }
          })

          // Determine actions for each role
          const rolesWithActions: any[] = []

          // Process new roles (insert or update)
          newRoleIds.forEach((roleChoiceId: string) => {
            if (!roleChoiceId) return

            const currentRole = currentRoleMap.get(roleChoiceId)
            const roleEntry: any = {
              [AccountRoleEntryFields.account_roles__account_role_choice_id]: roleChoiceId
            }

            if (currentRole && currentRole[AccountRoleEntryFields.account_roles__id]) {
              // Existing role - update action
              roleEntry[AccountRoleEntryFields.account_roles__id] =
                currentRole[AccountRoleEntryFields.account_roles__id]
              roleEntry[AccountRoleEntryFields.prog__account_roles__action] = 'update'
            } else {
              // New role - insert action (generate UUID on frontend)
              roleEntry[AccountRoleEntryFields.account_roles__id] = crypto.randomUUID()
              roleEntry[AccountRoleEntryFields.prog__account_roles__action] = 'insert'
            }

            rolesWithActions.push(roleEntry)
          })

          // Process removed roles (delete)
          currentRoleMap.forEach((currentRole: any, roleChoiceId: string) => {
            if (!newRoleMap.has(roleChoiceId)) {
              // Role was removed - delete action
              const roleId = currentRole[AccountRoleEntryFields.account_roles__id]
              if (roleId) {
                rolesWithActions.push({
                  [AccountRoleEntryFields.account_roles__id]: roleId,
                  [AccountRoleEntryFields.prog__account_roles__action]: 'delete'
                })
              }
            }
          })

          return {
            // Send as prog__account_roles array with actions
            [AccountRecordFields.prog__account_roles]: rolesWithActions
          }
        }
      }

      // Add patch configuration for round fields that need to update rounds table
      if (columnKey === 'valuation_round') {
        const recentRound = record[AccountRecordFields.prog__recent_round]
        const roundId = recentRound?.[RoundEntryFields.rounds__id] || null

        if (roundId) {
          selectFieldConfig.patchEndpoint = 'rounds'
          selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
            const result: any = {
              name: value,
              [RoundEntryFields.rounds__name]: value
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }
      } else if (columnKey === 'latest_round') {
        const latestRound = record[AccountRecordFields.prog__latest_round]
        const roundId = latestRound?.[RoundEntryFields.rounds__id] || null

        if (roundId) {
          selectFieldConfig.patchEndpoint = 'rounds'
          selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
            const result: any = {
              name: value,
              [RoundEntryFields.rounds__name]: value
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }
      } else if (columnKey === 'funding_stage') {
        const latestRound = record[AccountRecordFields.prog__latest_round]
        const roundId = latestRound?.[RoundEntryFields.rounds__id] || null

        if (roundId) {
          selectFieldConfig.patchEndpoint = 'rounds'
          selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
            const result: any = {
              funding_stage: value,
              [RoundEntryFields.rounds__funding_stage]: value
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }
      } else if (columnKey === RoundEntryFields.rounds__security_type || columnKey === 'rounds__security_type') {
        const latestRound = record[AccountRecordFields.prog__latest_round]
        const secRoundId = latestRound?.[RoundEntryFields.rounds__id] || null

        if (secRoundId) {
          selectFieldConfig.patchEndpoint = 'rounds'
          if (latestRound?.['rounds__security_type__label']) {
            selectFieldConfig.displayValue = latestRound['rounds__security_type__label']
          }
          selectFieldConfig.valueTransformer = (value: any) => {
            const result: any = {
              security_type: value,
              [RoundEntryFields.rounds__security_type]: value,
              id: secRoundId
            }
            return result
          }
        }
      }

      return selectFieldConfig

    case FieldTypeEnum.CITY:
      return {
        ...baseConfig,
        country: getCountryFilter(),
        contextKey: `cell-${record.id}-${columnKey}`,
        // Multi-field PATCH: Send city code, city name, and country code to API
        patchFields: [
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'cityName', targetField: 'business_city_label' },
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        // Transform city object into API fields + Redux fields
        valueTransformer: (cityObject: any, currentRecord?: any) => {
          // Handle clearing: when cityObject is empty string/null/undefined, set values to null explicitly
          const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

          // Extract values from CitySelect object: { label, value, name, countryCode, country, state }
          // Explicitly set to null when clearing (not undefined) so it's included in JSON
          const cityCode = isClearing ? null : (cityObject?.value ?? null)
          const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null

          // Only include countryCode when selecting a city (not when clearing)
          // When clearing city, preserve the existing country
          const countryCode = isClearing
            ? undefined
            : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
          const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

          const result: Record<string, any> = {
            // API fields (mapped by patchFields)
            cityCode: cityCode,
            cityName: cityLabel,
            state: state,
            // Redux fields (direct mapping)
            [AccountRecordFields.accounts__business_city_label]: cityLabel,
            [AccountRecordFields.accounts__business_stateprovince]: state
          }

          // Only include countryCode if we're selecting a city (not clearing)
          if (!isClearing && countryCode !== undefined) {
            result.countryCode = countryCode
            result[AccountRecordFields.accounts__business_country_code] = countryCode
          }

          return result
        }
      }

    case FieldTypeEnum.COUNTRY:
      return {
        ...baseConfig,
        contextKey: `cell-${record.id}-${columnKey}`,
        // Multi-field PATCH: Send country code and clear city
        patchFields: [
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        // Transform country value and clear city and state
        valueTransformer: (countryObject: any): CountryChangePatch => {
          // Extract country code from CountrySelect object: { label, value }
          // Handle clearing: when countryObject is empty string/null/undefined, set countryCode to null explicitly
          const isClearing =
            !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
          const countryCode = isClearing ? null : (countryObject?.value ?? null)
          const countryLabel = isClearing ? null : (countryObject?.label ?? null)

          return {
            // API fields (mapped by patchFields)
            // Explicitly set to null when clearing (not undefined) so it's included in JSON
            countryCode: countryCode,
            cityCode: null, // Clear city when country changes
            state: null, // Clear state when country changes
            // Redux fields (direct mapping)
            [AccountRecordFields.accounts__business_country_label]: countryLabel,
            [AccountRecordFields.accounts__business_city_label]: null,
            [AccountRecordFields.accounts__business_stateprovince]: null
          }
        }
      }

    case FieldTypeEnum.URL:
      return {
        ...baseConfig,
        placeholder: 'Enter website URL...'
      }

    case FieldTypeEnum.TEXTAREA:
      return {
        ...baseConfig,
        placeholder: 'Enter description...',
        rows: 3
      }

    case FieldTypeEnum.CONTACT: {
      const contactArray = Array.isArray(record[columnKey])
        ? record[columnKey]
        : record[columnKey]
          ? [record[columnKey]]
          : []

      const normalizedContacts = contactArray.map((contact: any) => {
        if (typeof contact === 'string') {
          return { value: contact, label: contact }
        }
        const contactId = contact?.contacts__id || contact?.id || contact?.value
        const displayName =
          contact?.prog__display_name ||
          `${contact?.contacts__first_name || ''} ${contact?.contacts__last_name || ''}`.trim() ||
          contact?.contacts__email ||
          contact?.label ||
          'Selected Contact'
        return {
          value: contactId,
          label: displayName,
          ...contact
        }
      })

      return {
        ...baseConfig,
        type: FieldTypeEnum.CONTACT,
        value: normalizedContacts,
        mode: 'multiple',
        tagDisplay: column.tagDisplay || false
      }
    }

    case FieldTypeEnum.TEXT:
      // Add patch configuration for round fields that need to update rounds table
      if (columnKey === 'valuation_round') {
        const recentRound = record[AccountRecordFields.prog__recent_round]
        const roundId = recentRound?.[RoundEntryFields.rounds__id] || null

        if (roundId) {
          return {
            ...baseConfig,
            patchEndpoint: 'rounds',
            patchId: roundId,
            valueTransformer: (value: any, currentRecord: any) => {
              const result: any = {
                name: value,
                [RoundEntryFields.rounds__name]: value
              }
              if (roundId) {
                result.id = roundId
              }
              return result
            }
          }
        }
      } else if (columnKey === 'latest_round') {
        const latestRound = record[AccountRecordFields.prog__latest_round]
        const roundId = latestRound?.[RoundEntryFields.rounds__id] || null

        if (roundId) {
          return {
            ...baseConfig,
            patchEndpoint: 'rounds',
            patchId: roundId,
            valueTransformer: (value: any, currentRecord: any) => {
              const result: any = {
                name: value,
                [RoundEntryFields.rounds__name]: value
              }
              if (roundId) {
                result.id = roundId
              }
              return result
            }
          }
        }
      }

      // Default TEXT field configuration
      return baseConfig

    case FieldTypeEnum.VALUATION:
      // For valuation, extract data from prog__recent_round
      if (columnKey === 'valuation') {
        const recentRound = record[AccountRecordFields.prog__recent_round]
        const roundId = recentRound?.[RoundEntryFields.rounds__id] || null

        const valuationConfig: any = {
          ...baseConfig,
          currencySymbol: getCurrencySymbol(),
          preMoneyValue: recentRound?.[RoundEntryFields.rounds__pre_money_valuation] || null,
          postMoneyValue: recentRound?.[RoundEntryFields.rounds__post_money_valuation] || null,
          min: 0,
          step: 0.01,
          showValuationType: true
        }

        // Add patching configuration when round ID is available
        if (roundId) {
          valuationConfig.patchEndpoint = 'rounds'
          valuationConfig.patchId = roundId
          valuationConfig.patchFields = [
            { sourceKey: 'preMoneyValue', targetField: 'pre_money_valuation' },
            { sourceKey: 'postMoneyValue', targetField: 'post_money_valuation' }
          ]
          valuationConfig.valueTransformer = (value: any, currentRecord: any) => {
            // Return source keys that patchFields expects, plus Redux field names
            const result: any = {
              // API fields (mapped by patchFields)
              preMoneyValue: value?.preMoneyValue || null,
              postMoneyValue: value?.postMoneyValue || null,
              // Redux fields (direct mapping)
              [RoundEntryFields.rounds__pre_money_valuation]: value?.preMoneyValue || null,
              [RoundEntryFields.rounds__post_money_valuation]: value?.postMoneyValue || null
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }

        return valuationConfig
      }
      return {
        ...baseConfig,
        currencySymbol: getCurrencySymbol(),
        min: 0,
        step: 0.01,
        showValuationType: true
      }

    default:
      return baseConfig
  }
}

/**
 * Maps validation errors from CompaniesTable format to InputManager format
 */
export function mapValidationErrors(cellEditErrors: Record<string, string>, recordId: string) {
  const validationErrors: Record<string, string> = {}

  Object.keys(cellEditErrors).forEach((errorKey) => {
    if (errorKey.startsWith(`${recordId}_`)) {
      const fieldKey = errorKey.replace(`${recordId}_`, '')
      validationErrors[fieldKey] = cellEditErrors[errorKey]
    }
  })

  return validationErrors
}

/**
 * Handles auto-population logic for related fields
 */
export function handleAutoPopulation(
  fieldKey: string,
  newValue: any,
  record: any,
  updateField: (key: string, value: any) => void,
  helpers: any
) {
  // City selection auto-populates country - extract from data.countryCode (matching deals pattern)
  if (
    fieldKey === AccountRecordFields.accounts__business_city_label ||
    fieldKey === AccountRecordFields.accounts__business_city_code ||
    fieldKey === 'accounts__business_city_code__label' ||
    fieldKey === 'business_city'
  ) {
    if (newValue) {
      const countryCode = newValue?.data?.countryCode || newValue?.countryCode
      if (countryCode) {
        updateField(AccountRecordFields.accounts__business_country_code, countryCode)
      }
    }
  }

  // Office city selection auto-populates office country
  if (fieldKey === 'accounts__office_city_label' || fieldKey === AccountRecordFields.accounts__office_city_code) {
    if (newValue) {
      const countryCode = newValue?.data?.countryCode || newValue?.countryCode
      if (countryCode) {
        updateField(AccountRecordFields.accounts__office_country_code, countryCode)
      }
    }
  }

  // Country change clears city
  if (
    fieldKey === AccountRecordFields.accounts__business_country_code ||
    fieldKey === AccountRecordFields.accounts__business_country_label ||
    fieldKey === 'accounts__business_country_code__label' ||
    fieldKey === 'business_country'
  ) {
    if (newValue) {
      const countryCode = newValue?.value || newValue
      if (countryCode) {
        updateField(AccountRecordFields.accounts__business_city_code, null)
        updateField(AccountRecordFields.accounts__business_city_label, null)
      }
    }
  }

  // Office country change clears office city
  if (fieldKey === AccountRecordFields.accounts__office_country_code || fieldKey === 'accounts__office_country_label') {
    if (newValue) {
      const countryCode = newValue?.value || newValue
      if (countryCode) {
        updateField(AccountRecordFields.accounts__office_city_code, null)
        updateField('accounts__office_city_label', null)
      }
    }
  }
}
